import { apiClient } from "../../../infra/api-client"
import { BASE_URL } from "../../../global"
import type { BmdStrand } from "../../bmd/types/bmd.types"
import type {
  EngDiagListResponse,
  EngDiagQuestion,
  EngDiagQuestionResponse,
  EngDiagStudentAnswers,
  EngDiagSubmitResponse,
} from "../types/eng-diag.types"

const ENDPOINTS = {
  listQuestions: "engDiag/listQuestions",
  getQuestion: "engDiag/getQuestion",
  getYearLevels: "engDiag/getYearLevels",
  previewQuestions: "engDiag/previewQuestions",
  getStrandsForYear: "engDiag/getStrandsForYear",
  getAssignedPreview: "engDiag/getAssignedPreview",
  getAnswersAwaitingMarking: "engDiag/getAnswersAwaitingMarking",
  markAnswer: "engDiag/markAnswer",
  getExistingForSession: "engDiag/getExistingForSession",
  createAssignment: "engDiag/createAssignment",
  submitAnswer: "engDiag/submitAnswer",
  getPrereqCandidates: "engDiag/getPrereqCandidates",
  previewPrereqAssignment: "engDiag/previewPrereqAssignment",
  createPrereqAssignment: "engDiag/createPrereqAssignment",
  getSkillBuilderCandidates: "engDiag/getSkillBuilderCandidates",
  previewSkillBuilderAssignment: "engDiag/previewSkillBuilderAssignment",
  createSkillBuilderAssignment: "engDiag/createSkillBuilderAssignment",
  getSkillBuilderPreview: "engDiag/getSkillBuilderPreview",
  getSkillBuilderAnswersAwaitingMarking:
    "engDiag/getSkillBuilderAnswersAwaitingMarking",
  markSkillBuilderAnswer: "engDiag/markSkillBuilderAnswer",
  getAudio: "engDiag/getAudio",
} as const

export type EngDiagYearLevel = {
  year_level_id: number
  level_name: string
  level_order: number
}

export type EngDiagYearLevelsResponse = {
  source: string
  year_levels: EngDiagYearLevel[]
}

/**
 * The clusters at a year, in the shape the math cluster picker takes: an
 * English cluster is a strand, an English topic is a topic. Sharing the type
 * is what lets the assign panel reuse BmdStrandPicker as-is.
 */
export type EngDiagStrandsResponse = {
  source: string
  year_level_id: number | null
  strands: BmdStrand[]
  /** Topics the student already has at this year, in any tutoring session. */
  locked_topic_ids: number[]
}

/** An English diagnostics already in this tutoring session, from engDiag/getExistingForSession. */
export type EngDiagAssignment = {
  pk_session_key: string
  pk_lesson_activity_key: string
  fk_lesson_key: string
  year_level_id: number
  level_name: string
  flow_status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  is_homework: boolean
  topic_count: number
  question_count: number
  started_on: string | null
  completed_on: string | null
  created_on: string
}

export type EngDiagExistingResponse = {
  source: string
  assignments: EngDiagAssignment[]
}

export type EngDiagCreateAssignmentResponse = {
  status: 0 | 1
  error?: string
  pk_session_key?: string
  pk_lesson_activity_key?: string
  fk_lesson_key?: string
  /** Topics and questions added by this call, not the session's totals. */
  topic_count?: number
  question_count?: number
  /** True when the topics went into a test already in this session. */
  merged?: boolean
  message?: string
}

export type EngDiagPreviewResponse = {
  source: string
  year_level_id: number | null
  question_count: number
  questions: EngDiagQuestion[]
  error?: string
}

/** Preview of an already-assigned test, from engDiag/getAssignedPreview. */
export type EngDiagAssignedPreviewResponse = EngDiagPreviewResponse & {
  level_name?: string
  flow_status?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  /**
   * Skill Builder preview only: the skill being practised. It takes the place of the year level
   * in the heading, because a Skill Builder IS one skill and the year is the less useful of the
   * two - and because the attachment does not put a level_name on the activity at all.
   */
  skill_name?: string
}

export async function listEngDiagQuestions(
  signal?: AbortSignal,
): Promise<EngDiagListResponse> {
  const response = await apiClient.post<EngDiagListResponse>(
    BASE_URL + ENDPOINTS.listQuestions,
    {},
    { signal },
  )
  return response.data
}

export async function getEngDiagQuestion(
  questionId: string,
  signal?: AbortSignal,
): Promise<EngDiagQuestionResponse> {
  const response = await apiClient.post<EngDiagQuestionResponse>(
    BASE_URL + ENDPOINTS.getQuestion,
    { question_id: questionId },
    { signal },
  )
  return response.data
}

export async function getEngDiagYearLevels(
  req: { subject_id: number; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagYearLevelsResponse> {
  const response = await apiClient.post<EngDiagYearLevelsResponse>(
    BASE_URL + ENDPOINTS.getYearLevels,
    req,
    { signal },
  )
  const data = response.data
  return {
    source: data?.source || "fixture",
    year_levels: Array.isArray(data?.year_levels) ? data.year_levels : [],
  }
}

/** Questions at a year; only those in `topic_ids` when given. */
export async function getEngDiagPreviewQuestions(
  req: { year_level_id: number; subject_id?: number; topic_ids?: number[] },
  signal?: AbortSignal,
): Promise<EngDiagPreviewResponse> {
  const response = await apiClient.post<EngDiagPreviewResponse>(
    BASE_URL + ENDPOINTS.previewQuestions,
    req,
    { signal },
  )
  return response.data
}

/**
 * The frozen questions of an assigned SKILL BUILDER, in serving order and in the option order
 * the child will see.
 *
 * A separate endpoint rather than a flag on getAssignedPreview: that one refuses any activity
 * that is not an English diagnostic, and relaxing the check to cover two activity types would
 * mean one handler guessing which session table to read.
 */
export async function getEngSkillBuilderPreview(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngDiagAssignedPreviewResponse> {
  const response = await apiClient.post<EngDiagAssignedPreviewResponse>(
    BASE_URL + ENDPOINTS.getSkillBuilderPreview,
    req,
    { signal },
  )
  return response.data
}

/** The frozen questions of an assigned test, in serving order. Refused unless the tutor shares an enrolment with the student. */
export async function getEngDiagAssignedPreview(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngDiagAssignedPreviewResponse> {
  const response = await apiClient.post<EngDiagAssignedPreviewResponse>(
    BASE_URL + ENDPOINTS.getAssignedPreview,
    req,
    { signal },
  )
  return response.data
}

export async function getEngDiagStrandsForYear(
  req: { year_level_id: number; student_key?: string },
  signal?: AbortSignal,
): Promise<EngDiagStrandsResponse> {
  const response = await apiClient.post<EngDiagStrandsResponse>(
    BASE_URL + ENDPOINTS.getStrandsForYear,
    req,
    { signal },
  )
  const data = response.data
  return {
    source: data?.source || "fixture",
    year_level_id: data?.year_level_id ?? null,
    strands: Array.isArray(data?.strands) ? data.strands : [],
    locked_topic_ids: Array.isArray(data?.locked_topic_ids)
      ? data.locked_topic_ids
      : [],
  }
}

export async function getEngDiagExistingForSession(
  req: { session_key: string; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagExistingResponse> {
  const response = await apiClient.post<EngDiagExistingResponse>(
    BASE_URL + ENDPOINTS.getExistingForSession,
    req,
    { signal },
  )
  return response.data
}

/**
 * Assigns topics at a year. Topics land in the test already at that year in
 * this session if there is one, unless force_new asks for a separate test.
 */
export async function createEngDiagAssignment(req: {
  student_key: string
  session_key: string
  lesson_key?: string
  year_level_id: number
  topic_ids: number[]
  is_homework?: 0 | 1
  force_new?: boolean
}): Promise<EngDiagCreateAssignmentResponse> {
  const response = await apiClient.post<EngDiagCreateAssignmentResponse>(
    BASE_URL + ENDPOINTS.createAssignment,
    req,
  )
  return response.data
}

export async function submitEngDiagAnswer(payload: {
  question_id: string
  student_answers: EngDiagStudentAnswers
  activity_key?: string
}): Promise<EngDiagSubmitResponse> {
  const response = await apiClient.post<EngDiagSubmitResponse>(
    BASE_URL + ENDPOINTS.submitAnswer,
    payload,
  )
  return response.data
}

/** A misconception the tutor can attribute a wrong answer to. */
export type EngDiagMisconceptionType = {
  code: string
  label: string
  description?: string | null
}

/**
 * One answer waiting on the tutor. `question.options` arrives whole here — unlike the student
 * endpoints, a tutor needs the accepted answers in front of them to judge what was written.
 *
 * `target_ref` is "" for the question as a whole, or a sub-question id for reading
 * comprehension, where each sub-question is marked on its own.
 */
export type EngDiagAnswerToMark = {
  pk_attempt_key: string
  session_question_key: string
  target_ref: string
  question: EngDiagQuestion
  response: Record<string, string>
  topic_name: string | null
  question_order: number
  skill_id: number | null
  skill_name: string | null
  answered_on: string
  duration_seconds: number | null
}

export type EngDiagMarkingResponse = {
  source: string
  error?: string
  pk_session_key?: string
  level_name?: string
  flow_status?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  misconception_types: EngDiagMisconceptionType[]
  answers: EngDiagAnswerToMark[]
}

export type EngDiagMarkAnswerResponse = {
  status: 0 | 1
  error?: string
  /** How many are still waiting after this one. */
  pending_count?: number
}

/** The answers in an assigned test that are waiting on a tutor. */
export async function getEngDiagAnswersAwaitingMarking(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngDiagMarkingResponse> {
  const response = await apiClient.post<EngDiagMarkingResponse>(
    BASE_URL + ENDPOINTS.getAnswersAwaitingMarking,
    req,
    { signal },
  )
  const data = response.data
  return {
    ...data,
    misconception_types: Array.isArray(data?.misconception_types) ? data.misconception_types : [],
    answers: Array.isArray(data?.answers) ? data.answers : [],
  }
}

/**
 * Record a verdict on one answer. Only once marked does it reach the report, the skill
 * builders and the prerequisites.
 */
export async function markEngDiagAnswer(req: {
  pk_activity_key: string
  attempt_key: string
  is_correct: boolean
  misconception_type_code?: string
  implicated_skill_ids?: number[]
}): Promise<EngDiagMarkAnswerResponse> {
  const response = await apiClient.post<EngDiagMarkAnswerResponse>(
    BASE_URL + ENDPOINTS.markAnswer,
    req,
  )
  return response.data
}

/**
 * The prerequisite test built from what a student got wrong in a base diagnostic.
 *
 * Its own activity on the lesson plan, not an extension of the test they already sat - the same
 * shape as the maths one. `skipped_no_question` lists skills a chain wants to ask about but has
 * no question written for, which is worth showing rather than hiding.
 */
export interface EngDiagPrereqCluster {
  cluster_id: number
  display_name: string
  question_count: number
  skills: { skill_id: number; skill_name: string | null; level: number }[]
}

export interface EngDiagPrereqPreview {
  status: number
  already_assigned: boolean
  pk_session_key: string | null
  pk_lesson_activity_key: string | null
  /** Every owed question, across all clusters. The panel totals the ticked ones itself. */
  question_count: number
  wrong_base_count: number
  /** Unfiltered: every cluster with something owed, so the tutor can add one left out before. */
  clusters: EngDiagPrereqCluster[]
  /** Clusters this test is already committed to, or null when it is unrestricted. */
  selected_cluster_ids: number[] | null
  skipped_no_question: { skill_id: number; skill_name: string | null }[]
  rules: { max_levels: number; follow_mode: string; multi_skill: boolean }
  error?: string
}

export interface EngDiagPrereqCreateResponse {
  status: number
  created: boolean
  pk_session_key: string | null
  pk_lesson_activity_key: string | null
  question_count: number
  skipped_no_question: { skill_id: number; skill_name: string | null }[]
  message?: string
  error?: string
}

/** A base diagnostic a prerequisite test could be built from. */
export interface EngDiagPrereqCandidate {
  pk_session_key: string
  level_name: string
  flow_status: string
  created_on: string
  completed_on: string | null
  base_question_count: number
  wrong_answer_count: number
  /** A prerequisite test already exists for it; assigning again only adds what is newly owed. */
  already_assigned: boolean
  prereq_session_key: string | null
}

/**
 * The diagnostics this student has sat that could seed a prerequisite test.
 *
 * Only ones with a wrong answer come back - there is nothing to chain from otherwise.
 */
export async function getEngDiagPrereqCandidates(
  req: { student_key: string },
  signal?: AbortSignal,
): Promise<{ status: number; candidates: EngDiagPrereqCandidate[]; error?: string }> {
  const response = await apiClient.post<{
    status: number
    candidates: EngDiagPrereqCandidate[]
    error?: string
  }>(BASE_URL + ENDPOINTS.getPrereqCandidates, req, { signal })
  return response.data
}

/** What a prerequisite test would contain right now. Writes nothing. */
export async function previewEngDiagPrereqAssignment(
  req: { parent_session_key: string; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagPrereqPreview> {
  const response = await apiClient.post<EngDiagPrereqPreview>(
    BASE_URL + ENDPOINTS.previewPrereqAssignment,
    req,
    { signal },
  )
  return response.data
}

/**
 * Create it, or add whatever the chains now owe.
 *
 * Safe to call again: it writes only questions that do not already exist, so running it after
 * the student has answered more of the base test adds the newly owed ones without duplicates.
 */
export async function createEngDiagPrereqAssignment(req: {
  parent_session_key: string
  student_key: string
  is_homework?: 0 | 1
  /** The sections the tutor ticked. Omitted means every section; empty is rejected server-side. */
  cluster_ids?: number[]
}): Promise<EngDiagPrereqCreateResponse> {
  const response = await apiClient.post<EngDiagPrereqCreateResponse>(
    BASE_URL + ENDPOINTS.createPrereqAssignment,
    req,
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Skill Builders
// ---------------------------------------------------------------------------

/**
 * Remediation for a skill the diagnostic found a gap in.
 *
 * A Skill Builder is EVERY QUESTION TAGGED TO ONE SKILL, and one activity is created per skill -
 * so the tutor ticks skills here, not clusters. The clusters below are only the sections the
 * picker groups those skills under.
 *
 * Not the maths Skill Builder, which is a PDF worksheet chosen from a list. Same word, same job,
 * different thing: `bmd/createSkillbuilderAssignment` takes a `skillbuilder_id`, this takes the
 * skills themselves.
 */
export interface EngDiagSkillBuilderSkill {
  skill_id: number
  skill_name: string | null
  /** Every live question tagged to this skill. The whole Skill Builder, not a sample. */
  question_count: number
  /** How many of those the tutor will have to mark by hand. */
  manual_marking_count: number
  /** Where the latest wrong answer's misconception sends the student, null when untagged. */
  routes_to: "skill_builder" | "prerequisite" | null
  misconception_type: string | null
  /** The child answered nothing. Still a gap, but worth saying so on the card. */
  was_skipped: boolean
  /** On the assigned test, rather than found underneath it by the prerequisite chain. */
  on_base_test: boolean
  cluster_id: number | null
  cluster_name: string | null
  topic_name: string | null
  sub_topic_name: string | null
  year_name: string | null
}

export interface EngDiagSkillBuilderCluster {
  cluster_id: number | null
  display_name: string
  skill_count: number
  question_count: number
  skills: EngDiagSkillBuilderSkill[]
}

export interface EngDiagSkillBuilderPreview {
  status: number
  source_session_key: string
  /** Settled answers read, for the "out of" in the summary. */
  answer_count: number
  failed_skill_count: number
  skill_count: number
  question_count: number
  manual_marking_count: number
  clusters: EngDiagSkillBuilderCluster[]
  skills: EngDiagSkillBuilderSkill[]
  /** Skills this student already has a Skill Builder for; they are not listed again above. */
  already_assigned_skill_ids: number[]
  /** A gap with no question written for it. The curriculum team's to close, not ours. */
  skipped_no_questions: { skill_id: number; skill_name: string | null }[]
  error?: string
}

export interface EngDiagSkillBuilderCreateResponse {
  status: number
  created: boolean
  activity_count?: number
  question_count?: number
  activities?: {
    created: boolean
    skill_id: number
    skill_name: string | null
    pk_sb_session_key: string
    pk_lesson_activity_key: string | null
    question_count: number
  }[]
  /** Ticked but no longer a gap - answered correctly since, or never failed. */
  not_owed?: number[]
  skipped_no_questions?: { skill_id: number; skill_name: string | null }[]
  message?: string
  error?: string
}

/** A diagnostic that Skill Builders could be built from. */
export interface EngDiagSkillBuilderCandidate {
  pk_session_key: string
  level_name: string
  flow_status: string
  created_on: string
  completed_on: string | null
  /** Wrong OR skipped: both count as a gap for remediation. */
  gap_answer_count: number
  /** How much remediation has already been built from this diagnostic. */
  skill_builder_count: number
}

/** The diagnostics this student has sat that have a gap worth remediating. */
export async function getEngDiagSkillBuilderCandidates(
  req: { student_key: string },
  signal?: AbortSignal,
): Promise<{
  status: number
  candidates: EngDiagSkillBuilderCandidate[]
  error?: string
}> {
  const response = await apiClient.post<{
    status: number
    candidates: EngDiagSkillBuilderCandidate[]
    error?: string
  }>(BASE_URL + ENDPOINTS.getSkillBuilderCandidates, req, { signal })
  return response.data
}

/** Which skills are owed a Skill Builder, and what each would contain. Writes nothing. */
export async function previewEngDiagSkillBuilderAssignment(
  req: { source_session_key: string; student_key: string },
  signal?: AbortSignal,
): Promise<EngDiagSkillBuilderPreview> {
  const response = await apiClient.post<EngDiagSkillBuilderPreview>(
    BASE_URL + ENDPOINTS.previewSkillBuilderAssignment,
    req,
    { signal },
  )
  return response.data
}

/**
 * Create one Skill Builder activity per ticked skill.
 *
 * Safe to call again. A skill that already has one is topped up with questions tagged since,
 * and no second activity appears; a skill that is no longer a gap is skipped and named in
 * `not_owed` rather than failing the call.
 *
 * Omitting `skill_ids` assigns everything owed. An EMPTY array is rejected server-side, because
 * it means the tutor ticked nothing.
 */
export async function createEngDiagSkillBuilderAssignment(req: {
  source_session_key: string
  student_key: string
  skill_ids?: number[]
  is_homework?: 0 | 1
  session_key?: string
  lesson_key?: string
}): Promise<EngDiagSkillBuilderCreateResponse> {
  const response = await apiClient.post<EngDiagSkillBuilderCreateResponse>(
    BASE_URL + ENDPOINTS.createSkillBuilderAssignment,
    req,
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Marking a Skill Builder
// ---------------------------------------------------------------------------

/**
 * One Skill Builder answer waiting on the tutor. `question.options` arrives whole, as it does
 * for a diagnostic: the tutor needs the accepted answers in front of them.
 *
 * Keyed by `sb_question_key` rather than `session_question_key` - a different table, and keeping
 * the diagnostics name would have invited passing one where the other was meant.
 */
export type EngSkillBuilderAnswerToMark = {
  pk_attempt_key: string
  sb_question_key: string
  target_ref: string
  question: EngDiagQuestion
  response: Record<string, string>
  question_order: number
  skill_id: number | null
  skill_name: string | null
  answered_on: string
  duration_seconds: number | null
}

export type EngSkillBuilderMarkingResponse = {
  source: string
  error?: string
  pk_sb_session_key?: string
  /** The skill being practised, which is what the whole activity is about. */
  skill_name?: string | null
  level_name?: string | null
  flow_status?: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED"
  misconception_types: EngDiagMisconceptionType[]
  answers: EngSkillBuilderAnswerToMark[]
}

/** The answers in an assigned Skill Builder that are waiting on a tutor. */
export async function getEngSkillBuilderAnswersAwaitingMarking(
  req: { pk_activity_key: string },
  signal?: AbortSignal,
): Promise<EngSkillBuilderMarkingResponse> {
  const response = await apiClient.post<EngSkillBuilderMarkingResponse>(
    BASE_URL + ENDPOINTS.getSkillBuilderAnswersAwaitingMarking,
    req,
    { signal },
  )
  return response.data
}

/**
 * Record a verdict on one Skill Builder answer.
 *
 * `misconception_type_code` is optional and dropped server-side for a correct answer. Nothing
 * downstream is earned by a wrong verdict here - a Skill Builder is already the remediation -
 * so unlike the diagnostic's equivalent this cannot grow anything.
 */
export async function markEngSkillBuilderAnswer(req: {
  pk_activity_key: string
  attempt_key: string
  is_correct: boolean
  misconception_type_code?: string
}): Promise<EngDiagMarkAnswerResponse> {
  const response = await apiClient.post<EngDiagMarkAnswerResponse>(
    BASE_URL + ENDPOINTS.markSkillBuilderAnswer,
    req,
  )
  return response.data
}

/**
 * The bytes of one audio clip in a diagnostics question.
 *
 * Clips live in the database, not on a CDN, and the endpoint needs the tutor's token - so a
 * plain <audio src> cannot reach them. The caller turns this blob into an object URL.
 */
export async function fetchEngDiagAudio(
  audioId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiClient.post(
    BASE_URL + ENDPOINTS.getAudio,
    { audio_id: audioId },
    { responseType: "blob", signal },
  )
  return response.data as Blob
}
